<script setup>
import { computed, watch } from "vue";
import ArgonSelect from "@/components/ArgonSelect.vue";
import {
  escapeHtml,
  getAvatarText,
  normalizeHexColor,
  toSoftColorFromHex,
} from "@/utils/formatters";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: [Array, String, Number],
    default: "",
  },
  options: {
    type: Array,
    default() {
      return [];
    },
  },
  placeholder: {
    type: String,
    default: "",
  },
  filterable: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  menuWidth: {
    type: String,
    default: "220",
  },
  error: {
    type: Boolean,
    default: false,
  },
  size:{
    type: String,
    default: "md",
  },
});

const emit = defineEmits(["update:modelValue", "selected-change"]);

function toComparableValue(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function buildVehicleOptionLabel(option) {
  const avatarText = escapeHtml(
    getAvatarText(option?.label, option?.abbreviation)
  );
  const vehicleName = escapeHtml(option?.label || "Vehiculo");
  const accent = normalizeHexColor(option?.color, "#5baee9");
  const soft = toSoftColorFromHex(accent, 0.18);
  const sheen = `color-mix(in srgb, white 88%, ${accent})`;
  const border = `color-mix(in srgb, ${accent} 22%, white)`;
  const shadow = `color-mix(in srgb, ${accent} 20%, transparent)`;

  return `<span class='vehicle-option-inline' style='--vehicle-option-accent:${accent};--vehicle-option-soft:${soft};--vehicle-option-sheen:${sheen};--vehicle-option-border:${border};--vehicle-option-shadow:${shadow};'><span class='vehicle-option-avatar'>${avatarText}</span><span class='vehicle-option-name'>${vehicleName}</span></span>`;
}

const optionsWithAvatar = computed(() =>
  props.options.map((option) => ({
    ...option,
    label: buildVehicleOptionLabel(option),
    image: "",
  }))
);

const selectedOption = computed(() => {
  if (props.multiple) {
    const selectedValues = Array.isArray(props.modelValue)
      ? props.modelValue.map((value) => toComparableValue(value))
      : [];
    if (selectedValues.length === 0) return [];

    return props.options.filter((option) => selectedValues.includes(toComparableValue(option?.value)));
  }

  const target = toComparableValue(props.modelValue);
  if (!target) return null;

  return props.options.find((option) => toComparableValue(option?.value) === target) || null;
});

watch(
  () => selectedOption.value,
  (value) => {
    emit("selected-change", value || null);
  },
  { immediate: true }
);

function onModelValueUpdate(value) {
  emit("update:modelValue", value);

  if (props.multiple) {
    const selectedValues = Array.isArray(value) ? value.map((item) => toComparableValue(item)) : [];
    const selected = props.options.filter((option) => selectedValues.includes(toComparableValue(option?.value)));
    emit("selected-change", selected);
    return;
  }

  const target = toComparableValue(value);
  const selected = props.options.find((option) => toComparableValue(option?.value) === target) || null;
  emit("selected-change", selected);
}
</script>

<template>
  <div class="vehicle-select-with-avatar">
    <argon-select
      :id="id"
      class="col"
      :model-value="modelValue"
      :options="optionsWithAvatar"
      :placeholder="placeholder"
      :filterable="filterable"
      :disabled="disabled"
      :multiple="multiple"
      :menu-width="menuWidth"
      :error="error"
      :size="size"
      @update:model-value="onModelValueUpdate"
    />
  </div>
</template>

<style scoped lang="scss">
.vehicle-select-with-avatar :deep(.form-group),
.vehicle-select-with-avatar :deep(.choices) {
  width: 100%;
}

.vehicle-select-with-avatar :deep(.choices__list--dropdown) {
  z-index: 1030 !important;
}

.vehicle-select-with-avatar :deep(.choices.is-invalid .choices__inner),
.vehicle-select-with-avatar :deep(.choices.is-invalid.is-focused .choices__inner),
.vehicle-select-with-avatar :deep(.choices.is-invalid.is-open .choices__inner) {
  border-color: #dc3545 !important;
}

.vehicle-select-with-avatar :deep(.choice-label .vehicle-option-inline) {
  align-items: center;
  display: inline-flex;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
}

.vehicle-select-with-avatar :deep(.choice-label .vehicle-option-avatar) {
  align-items: center;
  background:
    radial-gradient(circle at 30% 28%, var(--vehicle-option-sheen, rgba(255, 255, 255, 0.9)) 0%, transparent 48%),
    linear-gradient(180deg, var(--vehicle-option-sheen, rgba(255, 255, 255, 0.9)) 0%, var(--vehicle-option-soft, rgba(91, 174, 233, 0.18)) 100%);
  border: 1px solid var(--vehicle-option-border, rgba(91, 174, 233, 0.25));
  border-radius: 999px;
  color: var(--vehicle-option-accent, #5baee9);
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.66rem;
  font-weight: 700;
  height: 25px;
  justify-content: center;
  letter-spacing: 0.04em;
  line-height: 1;
  min-width: 25px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.75),
    0 6px 14px -10px var(--vehicle-option-shadow, transparent);
  overflow: hidden;
  padding: 0;
  text-align: center;
  text-transform: uppercase;
  user-select: none;
  white-space: nowrap;
  width: 25px;
}

.vehicle-select-with-avatar :deep(.choice-label .vehicle-option-name) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vehicle-select-with-avatar :deep(.form-control-xs .choice-label .vehicle-option-inline) {
  gap: 4px;
}

.vehicle-select-with-avatar :deep(.form-control-xs .choice-label .vehicle-option-avatar) {
  font-size: 0.5rem;
  height: 18px;
  letter-spacing: 0.03em;
  min-width: 18px;
  width: 18px;
}

.vehicle-select-with-avatar :deep(.form-control-xs .choice-label .vehicle-option-name) {
  font-size: 0.64rem;
  line-height: 1.1;
}
</style>
